import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown, Calendar } from "lucide-react";

const HistoryDateFilter = ({ onRangeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("last6months");
  const [showCustomPickers, setShowCustomPickers] = useState(false);
  const [customStartMonth, setCustomStartMonth] = useState(null);
  const [customEndMonth, setCustomEndMonth] = useState(null);
  const dropdownRef = useRef(null);

  const getEndDate = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() - 1, 1);
  };

  const formatMonth = (year, month) => {
    const monthStr = String(month).padStart(2, '0');
    return `01/${monthStr}/${year}`;
  };

  const calculateMonthRange = (option) => {
    const endDate = getEndDate();
    const endYear = endDate.getFullYear();
    const endMonth = endDate.getMonth() + 1;
    
    let startDate = new Date(endDate);
    
    switch(option) {
      case "last3months":
        startDate.setMonth(endDate.getMonth() - 2);
        break;
      case "last6months":
        startDate.setMonth(endDate.getMonth() - 5);
        break;
      case "last9months":
        startDate.setMonth(endDate.getMonth() - 8);
        break;
      case "lastyear":
        startDate.setMonth(endDate.getMonth() - 11);
        break;
      default:
        startDate.setMonth(endDate.getMonth() - 5);
    }
    
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth() + 1;
    
    return {
      startDate: formatMonth(startYear, startMonth),
      endDate: formatMonth(endYear, endMonth)
    };
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setShowCustomPickers(false);
    
    if (option === "custom") {
      setShowCustomPickers(true);
      setCustomStartMonth(null);
      setCustomEndMonth(null);
    } else {
      const { startDate, endDate } = calculateMonthRange(option);
      onRangeChange(startDate, endDate);
      setIsOpen(false);
    }
  };

  const handleCustomApply = () => {
    if (customStartMonth && customEndMonth) {
      const startYear = customStartMonth.getFullYear();
      const startMonth = customStartMonth.getMonth() + 1;
      const endYear = customEndMonth.getFullYear();
      const endMonth = customEndMonth.getMonth() + 1;
      
      const startDate = formatMonth(startYear, startMonth);
      const endDate = formatMonth(endYear, endMonth);
      
      onRangeChange(startDate, endDate);
      setIsOpen(false);
      setShowCustomPickers(false);
    }
  };

  useEffect(() => {
    const { startDate, endDate } = calculateMonthRange("last6months");
    onRangeChange(startDate, endDate);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustomPickers(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getButtonLabel = () => {
    if (selectedOption === "custom") return "Custom Range";
    switch(selectedOption) {
      case "last3months": return "Last 3 Months";
      case "last6months": return "Last 6 Months";
      case "last9months": return "Last 9 Months";
      case "lastyear": return "Last Year";
      default: return "Last 6 Months";
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="ml-4 inline-flex items-center gap-2 bg-primary-cyan hover:bg-primary-cyan/85 text-white px-5 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
      >
        <Calendar size={18} />
        <span>{getButtonLabel()}</span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-20 min-w-[220px] overflow-hidden">
          <div className="py-2">
            <button
              onClick={() => handleOptionClick("last3months")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last3months" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 3 Months
            </button>
            <button
              onClick={() => handleOptionClick("last6months")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last6months" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 6 Months
            </button>
            <button
              onClick={() => handleOptionClick("last9months")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last9months" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 9 Months
            </button>
            <button
              onClick={() => handleOptionClick("lastyear")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "lastyear" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last Year
            </button>
            <div className="border-t border-gray-100 my-1"></div>
            <button
              onClick={() => handleOptionClick("custom")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "custom" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Custom Range
            </button>
          </div>

          {showCustomPickers && (
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Start Month</label>
                <DatePicker
                  selected={customStartMonth}
                  onChange={(date) => setCustomStartMonth(date)}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  maxDate={customEndMonth || new Date()}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                  placeholderText="Select start month"
                />
              </div>
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">End Month</label>
                <DatePicker
                  selected={customEndMonth}
                  onChange={(date) => setCustomEndMonth(date)}
                  dateFormat="MM/yyyy"
                  showMonthYearPicker
                  maxDate={new Date()}
                  minDate={customStartMonth}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                  placeholderText="Select end month"
                />
              </div>
              <button
                onClick={handleCustomApply}
                disabled={!customStartMonth || !customEndMonth}
                className="w-full bg-primary-cyan hover:bg-primary-cyan/85 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow"
              >
                Apply Range
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HistoryDateFilter;